/**
 * Funnels Hooks - Data fetching and mutations for the Funnel Builder
 * 
 * Uses TanStack Query for:
 * - List funnels
 * - Get single funnel
 * - Create/Update/Delete funnels
 * - Draft/Publish workflow
 * - Node operations
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "@/hooks/use-toast";

// Query Keys
export const funnelKeys = {
  all: ["funnels"],
  list: () => [...funnelKeys.all, "list"],
  detail: (id) => [...funnelKeys.all, "detail", id],
};

/**
 * Fetch all funnels
 */
export function useFunnels() {
  return useQuery({
    queryKey: funnelKeys.list(),
    queryFn: async () => {
      const { data } = await api.get("/funnels");
      return data;
    },
  });
}

/**
 * Fetch single funnel by ID
 */
export function useFunnel(id) {
  return useQuery({
    queryKey: funnelKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get(`/funnels/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

/**
 * Create a new funnel
 */
export function useCreateFunnel() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post("/funnels", payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: funnelKeys.list() });
      toast({ title: "Funnel created successfully" });
    },
    onError: (error) => {
      const message = error.response?.data?.detail || "Failed to create funnel";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });
}

/**
 * Update funnel metadata
 */
export function useUpdateFunnel() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const { data } = await api.patch(`/funnels/${id}`, payload);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: funnelKeys.list() });
      queryClient.invalidateQueries({ queryKey: funnelKeys.detail(data.id) });
    },
    onError: (error) => {
      const message = error.response?.data?.detail || "Failed to update funnel";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });
}

/**
 * Delete a funnel
 */
export function useDeleteFunnel() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(`/funnels/${id}`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: funnelKeys.list() });
      toast({ title: "Funnel deleted" });
    },
    onError: (error) => {
      const message = error.response?.data?.detail || "Failed to delete funnel";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });
}

/**
 * Duplicate a funnel
 */
export function useDuplicateFunnel() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, name, slug }) => {
      const { data } = await api.post(`/funnels/${id}/duplicate`, { name, slug });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: funnelKeys.list() });
      toast({ title: "Funnel duplicated" });
    },
    onError: (error) => {
      const message = error.response?.data?.detail || "Failed to duplicate funnel";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });
}

/**
 * P4-R2C: read a message out of an API error whose `detail` may be a string
 * OR a structured object.
 *
 * `PUT /draft` now answers a rejected save with
 * `detail: { code, message, ... }`. Passing that straight into a toast
 * renders an object, so every consumer needs the same unwrapping -- it lives
 * here once rather than being repeated at each call site.
 */
export function apiErrorMessage(error, fallback = "Request failed") {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string" && detail.message) return detail.message;
    if (typeof detail.code === "string" && detail.code) return detail.code;
  }
  if (typeof error?.message === "string" && error.message) return error.message;
  return fallback;
}

/** The machine-readable code, when the server supplied one. */
export function apiErrorCode(error) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === "object" && typeof detail.code === "string") {
    return detail.code;
  }
  return null;
}

/**
 * A stale-version rejection of a draft save, identified by STATUS AND CODE.
 *
 * Status alone is not enough: 409 is also how a malformed stored version and
 * a vanished funnel are reported, and those are not resolved by refreshing.
 */
export function isDraftVersionConflict(error) {
  return error?.response?.status === 409
    && apiErrorCode(error) === "DRAFT_VERSION_CONFLICT";
}

/**
 * P4-FIX-797. THE THREE OUTCOMES OF A DRAFT SAVE.
 *
 * `PUT /draft` answers with one of three things, and the whole defect was
 * collapsing them into two:
 *
 *   CONFIRMED SAVED      `detail.draft_committed === true`
 *                        The write is durable; only the response body is
 *                        unavailable. Codes: DRAFT_SAVED_RESPONSE_UNAVAILABLE,
 *                        FUNNEL_VANISHED_AFTER_SAVE,
 *                        DRAFT_SAVED_VERSION_UNREPRESENTABLE.
 *                        Adopt the version. Never say "not saved". Never retry.
 *   OUTCOME UNCERTAIN    `detail.draft_committed === null`
 *                        Code DRAFT_SAVE_OUTCOME_UNCERTAIN. The write may or
 *                        may not have landed. Refetch and compare; never retry.
 *   CONFIRMED NOT SAVED  no `draft_committed` at all -- an ordinary rejection
 *                        that provably did not write.
 *
 * Read via `=== true` / `=== null` rather than truthiness, because `null` and
 * `undefined` are the two states that must NOT be conflated: `null` means
 * unknown and `undefined` means "the server did not write".
 */
export function isDraftSaveCommitted(error) {
  const detail = error?.response?.data?.detail;
  return !!detail && typeof detail === "object" && detail.draft_committed === true;
}

/** The outcome is genuinely unknown -- neither saved nor not saved. */
export function isDraftSaveUncertain(error) {
  const detail = error?.response?.data?.detail;
  if (!detail || typeof detail !== "object") return false;
  return detail.draft_committed === null
    && detail.code === "DRAFT_SAVE_OUTCOME_UNCERTAIN";
}

/** The version a committed-but-unavailable response reports, when it has one. */
export function committedDraftVersion(error) {
  const v = error?.response?.data?.detail?.current_draft_version;
  return Number.isInteger(v) ? v : null;
}

/**
 * A stable signature of the draft CONTENT a save sent or the server stored.
 *
 * Compares only what the CLIENT controls and the server round-trips: node ids
 * in order with each node's `content`, and edge TOPOLOGY in order.
 *
 * EDGE IDS ARE DELIBERATELY EXCLUDED. `save_draft` REGENERATES them --
 * measured against the live endpoint, `e1`/`e2` came back as
 * `edge_1961801a635b`/`edge_440c2173e33e` for a save that committed
 * perfectly. Including them made the signature of a genuine commit differ
 * from the payload that produced it, which would have left
 * `reconcileDraftSave` permanently answering "indeterminate" -- the
 * reconciliation path dead, and the uncertainty never resolved. The
 * non-vacuity test over the captured real pair exists to keep that honest.
 *
 * `source_node_id`/`target_node_id`/`kind` ARE preserved verbatim, so edge
 * topology is still compared and a rewired graph is still detected. Key order
 * is normalized so two equal objects always agree.
 */
export function draftContentSignature(draft) {
  const stable = (v) => {
    if (Array.isArray(v)) return v.map(stable);
    if (v && typeof v === "object") {
      return Object.keys(v).sort().reduce((acc, k) => { acc[k] = stable(v[k]); return acc; }, {});
    }
    return v;
  };
  const nodes = Array.isArray(draft?.nodes) ? draft.nodes : [];
  const edges = Array.isArray(draft?.edges) ? draft.edges : [];
  return JSON.stringify({
    nodes: nodes.map((n) => ({ id: n?.id ?? null, content: stable(n?.content ?? null) })),
    edges: edges.map((e) => ({
      from: e?.source_node_id ?? null,
      to: e?.target_node_id ?? null,
      kind: e?.kind ?? null,
    })),
  });
}

/**
 * P4-FIX-797 / D3. WHERE EACH SAVED FIELD LANDS IN `GET /funnels/{id}`.
 *
 * Measured against the live endpoint, not assumed -- the destinations are not
 * uniform and two of them are actively surprising:
 *
 *   nodes, edges, entry_node_id, header, settings, admin  ->  `draft.*`
 *   funnel_settings                                       ->  TOP-LEVEL `settings`
 *   appearance, appearance_overrides, styling_mode,
 *   builder_data                                          ->  top level
 *
 * `funnel_settings` landing on top-level `settings` while `settings` lands on
 * `draft.settings` is the trap: a naive same-name lookup would compare the
 * wrong pair and silently answer "indeterminate" forever.
 *
 * A field NOT in this map has no verifiable destination, so a payload carrying
 * one can never be attributed -- `draftSubmissionMatches` returns false rather
 * than ignoring it.
 */
const SAVED_FIELD_LOCATIONS = {
  nodes: (r) => r?.draft?.nodes,
  edges: (r) => r?.draft?.edges,
  entry_node_id: (r) => r?.draft?.entry_node_id,
  header: (r) => r?.draft?.header,
  settings: (r) => r?.draft?.settings,
  admin: (r) => r?.draft?.admin,
  funnel_settings: (r) => r?.settings,
  appearance: (r) => r?.appearance,
  appearance_overrides: (r) => r?.appearance_overrides,
  styling_mode: (r) => r?.styling_mode,
  builder_data: (r) => r?.builder_data,
};

//: not content: routing and concurrency control, never compared.
const NON_CONTENT_FIELDS = new Set(["id", "expected_draft_version"]);

function stableValue(v) {
  if (Array.isArray(v)) return v.map(stableValue);
  if (v && typeof v === "object") {
    return Object.keys(v).sort().reduce((acc, k) => {
      acc[k] = stableValue(v[k]); return acc;
    }, {});
  }
  return v;
}
const sameValue = (a, b) =>
  JSON.stringify(stableValue(a)) === JSON.stringify(stableValue(b));

/**
 * Does the stored funnel contain EVERY field this save submitted?
 *
 * P4-FIX-797 / D3. The first implementation compared only nodes and edges, and
 * that is demonstrably not enough. Staged on the live endpoint: editor A
 * submits an APPEARANCE-ONLY change at version 6 and does not commit; editor B
 * commits a HEADER-only change, taking the funnel to 7 with byte-identical
 * nodes. A then sees `version === expected + 1` and matching nodes, concludes
 * "committed", clears its unsaved state -- and A's colour change is gone. A
 * nodes-and-edges comparison attributes another editor's commit to this one
 * whenever the edit lives anywhere else in the payload.
 *
 * Every submitted field must therefore be accounted for:
 *   - `nodes`     compared by id + `content`; sibling metadata the server may
 *                 add or reorder is ignored.
 *   - `edges`     compared by TOPOLOGY only. `save_draft` regenerates edge ids
 *                 through a deterministic topology hash, so comparing ids
 *                 would never match a real commit.
 *   - `appearance` compared as a SUBSET: the server merges brand defaults into
 *                 it, so the stored object legitimately has more keys -- but
 *                 every key we sent must be present with the value we sent.
 *                 A key the backend whitelist dropped therefore fails, which
 *                 is correct: we cannot prove that write landed.
 *   - everything else compared by normalized deep equality.
 *
 * Returns false -- never "assume committed" -- for any field it cannot locate.
 */
export function draftSubmissionMatches(sent, stored) {
  if (!sent || typeof sent !== "object" || !stored || typeof stored !== "object") {
    return false;
  }
  for (const [field, value] of Object.entries(sent)) {
    if (NON_CONTENT_FIELDS.has(field)) continue;
    if (value === undefined) continue;

    const locate = SAVED_FIELD_LOCATIONS[field];
    //: an unmappable field is not evidence of anything
    if (!locate) return false;
    const storedValue = locate(stored);

    if (field === "nodes") {
      if (!sameValue(projectNodes(value), projectNodes(storedValue))) return false;
    } else if (field === "edges") {
      if (!sameValue(projectEdges(value), projectEdges(storedValue))) return false;
    } else if (field === "appearance") {
      //: subset, for the brand-default merge described above
      if (!value || typeof value !== "object") {
        if (!sameValue(value, storedValue)) return false;
      } else {
        if (!storedValue || typeof storedValue !== "object") return false;
        for (const [k, v] of Object.entries(value)) {
          if (!sameValue(v, storedValue[k])) return false;
        }
      }
    } else if (!sameValue(value, storedValue)) {
      return false;
    }
  }
  return true;
}

function projectNodes(nodes) {
  return (Array.isArray(nodes) ? nodes : []).map((n) => ({
    id: n?.id ?? null, content: stableValue(n?.content ?? null),
  }));
}
function projectEdges(edges) {
  return (Array.isArray(edges) ? edges : []).map((e) => ({
    from: e?.source_node_id ?? null,
    to: e?.target_node_id ?? null,
    kind: e?.kind ?? null,
  }));
}

/**
 * BOUNDED reconciliation of an uncertain outcome, using only existing reads.
 *
 * The CAS makes `N -> N+1` EXCLUSIVE -- measured: six concurrent saves all
 * claiming version 5 produced one accepted and five refused -- so if the
 * funnel now reads N+1 and the stored draft is the one we sent, our save is
 * the write that moved it, and it committed.
 *
 * Returns "committed" | "not-committed" | "indeterminate".
 *
 * Bounded on purpose. Only two situations are decidable:
 *   version === expected      -> nothing moved, ours did not commit
 *   version === expected + 1 AND the stored draft is ours -> ours committed
 * Anything else -- the version has run further ahead, the content does not
 * match, or the funnel cannot even be read -- stays INDETERMINATE. Guessing
 * in either direction is how an edit gets re-applied over someone else's
 * work, so the uncertainty is kept rather than resolved optimistically.
 *
 * No new persistent field is required for this, and none is added.
 */
export async function reconcileDraftSave(funnelId, expectedVersion, sentDraft) {
  if (!funnelId || !Number.isInteger(expectedVersion)) return "indeterminate";
  let data;
  try {
    ({ data } = await api.get(`/funnels/${funnelId}`));
  } catch (error) {
    //: the read can fail for the very reason the save could not be returned
    //: (a malformed stored funnel breaks this endpoint too), so this is
    //: explicitly NOT evidence that the write did not land.
    console.error("Failed to reconcile draft save outcome:", error);
    return "indeterminate";
  }
  const current = data?.draft_version;
  if (!Number.isInteger(current)) return "indeterminate";
  if (current === expectedVersion) return "not-committed";
  if (current === expectedVersion + 1) {
    //: EVERY submitted field must be accounted for, not just nodes and edges
    //: -- see `draftSubmissionMatches` for the measured scenario this closes.
    return draftSubmissionMatches(sentDraft, data) ? "committed" : "indeterminate";
  }
  return "indeterminate";
}

/** The authoritative current version a conflict response carries, if any. */
export function conflictCurrentVersion(error) {
  const v = error?.response?.data?.detail?.current_draft_version;
  return Number.isInteger(v) ? v : null;
}

/**
 * P4-R2C: a version-aware, serialized draft writer for callers that hold no
 * version state of their own.
 *
 * The Profit Path Builder writes this endpoint from four places -- theme sync,
 * consent patch, booking patch, appearance sync -- and tracked no funnel
 * version at all, so making `expected_draft_version` required would have made
 * every one of them a 400. Three of those sites swallowed their errors in a
 * `catch` that only logged, so the failure would have been invisible and
 * consent language would simply have stopped syncing.
 *
 * Rather than thread version state through a page that has no concept of it,
 * the authoritative version is cached here per funnel id and is only ever
 * SERVER-SUPPLIED: seeded from the funnel detail, then replaced with whatever
 * each save returns. Nothing here computes the next version.
 *
 * Writes are serialized per funnel id. Two overlapping writes from one Builder
 * instance would otherwise both send the same cached version, and the second
 * would be rejected as a conflict with itself.
 *
 * On a conflict the cached version is DISCARDED rather than advanced, so the
 * next write must re-read the authoritative value, and the rejected payload is
 * NOT retried -- a retry would push local content over the newer server state
 * that caused the conflict.
 */
const draftVersionCache = new Map();   // funnelId -> server-reported version
const draftWriteChains = new Map();    // funnelId -> tail of the write chain

/** Discard a cached version, forcing the next write to re-establish it. */
export function forgetFunnelDraftVersion(funnelId) {
  draftVersionCache.delete(funnelId);
}

/** Seed the cache from a response that already carries an authoritative version. */
export function adoptFunnelDraftVersion(funnelId, version) {
  if (funnelId && Number.isInteger(version)) {
    draftVersionCache.set(funnelId, version);
    return true;
  }
  return false;
}

/** Read the authoritative version from the server. Never guesses. */
async function establishFunnelDraftVersion(funnelId) {
  const { data } = await api.get(`/funnels/${funnelId}`);
  const version = data?.draft_version;
  if (!Number.isInteger(version)) {
    throw new Error(
      `funnel ${funnelId} did not report an integer draft_version; refusing to save`
    );
  }
  draftVersionCache.set(funnelId, version);
  return version;
}

/**
 * Save a draft patch under the server's compare-and-swap.
 *
 * Returns `{ ok, conflict, data, error, currentVersion }` and never throws, so
 * a caller that previously swallowed errors can surface the outcome without
 * restructuring its control flow. `label` names the thing being synced so the
 * message says what did not save.
 */
export async function saveFunnelDraftVersioned(funnelId, payload, { label = "Changes", notify = true } = {}) {
  if (!funnelId) {
    return { ok: false, conflict: false, error: new Error("no funnel id") };
  }

  const run = async () => {
    let expected = draftVersionCache.get(funnelId);
    if (!Number.isInteger(expected)) {
      try {
        expected = await establishFunnelDraftVersion(funnelId);
      } catch (error) {
        if (notify) {
          toast({
            title: `${label} not saved`,
            description: apiErrorMessage(
              error, "Could not read this funnel's current version."),
            variant: "destructive",
          });
        }
        return { ok: false, conflict: false, error };
      }
    }

    try {
      const { data } = await api.put(`/funnels/${funnelId}/draft`, {
        ...payload,
        expected_draft_version: expected,
      });
      adoptFunnelDraftVersion(funnelId, data?.draft_version);
      return { ok: true, conflict: false, data };
    } catch (error) {
      if (isDraftVersionConflict(error)) {
        const currentVersion = conflictCurrentVersion(error);
        // Discard, do not advance: the next write re-reads the authoritative
        // value. The rejected payload is deliberately not retried.
        forgetFunnelDraftVersion(funnelId);
        if (notify) {
          toast({
            title: `${label} not saved`,
            description:
              "This funnel changed in another session, so these changes were not applied. "
              + "Reload the funnel and apply them again.",
            variant: "destructive",
          });
        }
        return { ok: false, conflict: true, error, currentVersion };
      }
      // P4-FIX-797. A CONFIRMED COMMIT is not a failure.
      //
      // `draft_committed: true` means the write is durable and only the body
      // could not be returned. Reporting it as `${label} not saved` was the
      // acceptance-criterion-3 violation: the toast title said "not saved"
      // while its own body said "The draft WAS saved". Worse, leaving the
      // cached version stale made the user's next save collide with their OWN
      // committed write and be told "another session" changed the funnel --
      // which invites a refresh-and-re-apply that destroys a concurrent
      // editor's work.
      if (isDraftSaveCommitted(error)) {
        const committedVersion = committedDraftVersion(error);
        if (committedVersion !== null) {
          adoptFunnelDraftVersion(funnelId, committedVersion);
        } else {
          //: committed at a version we cannot name -- discard rather than keep
          //: a value we know is stale, so the next write re-reads.
          forgetFunnelDraftVersion(funnelId);
        }
        if (notify) {
          toast({
            title: `${label} saved`,
            description: apiErrorMessage(
              error, "Saved, but the updated funnel could not be returned. Reload to see it."),
          });
        }
        return { ok: true, conflict: false, committed: true, uncertain: false,
                 currentVersion: committedVersion, error };
      }

      // P4-FIX-797. A GENUINELY UNCERTAIN outcome is resolved by READING, not
      // by retrying -- this endpoint is not idempotent, so a retry advances
      // the version a second time for one edit.
      if (isDraftSaveUncertain(error)) {
        const verdict = await reconcileDraftSave(funnelId, expected, payload);
        if (verdict === "committed") {
          adoptFunnelDraftVersion(funnelId, expected + 1);
          if (notify) {
            toast({
              title: `${label} saved`,
              description:
                "The database did not confirm the save, but the change is present.",
            });
          }
          return { ok: true, conflict: false, committed: true, uncertain: false,
                   currentVersion: expected + 1, error };
        }
        //: Either way the cached version is discarded, so the next write
        //: re-reads the authoritative value instead of colliding with our own
        //: possible commit and being blamed on another session.
        forgetFunnelDraftVersion(funnelId);
        if (verdict === "not-committed") {
          if (notify) {
            toast({
              title: `${label} not saved`,
              description:
                "The database did not apply this change. Nothing was saved -- you can try again.",
              variant: "destructive",
            });
          }
          return { ok: false, conflict: false, committed: false, uncertain: false, error };
        }
        //: INDETERMINATE -- retained, never guessed.
        if (notify) {
          toast({
            title: `${label} outcome unknown`,
            description:
              "The save was sent but could not be confirmed. Reload the funnel to see "
              + "whether the change is there -- do not re-apply it blindly.",
          });
        }
        return { ok: false, conflict: false, committed: null, uncertain: true, error };
      }

      if (notify) {
        toast({
          title: `${label} not saved`,
          description: apiErrorMessage(error, `Could not save ${label.toLowerCase()}.`),
          variant: "destructive",
        });
      }
      return { ok: false, conflict: false, committed: false, uncertain: false, error };
    }
  };

  const previous = draftWriteChains.get(funnelId) || Promise.resolve();
  const chained = previous.then(run, run);
  draftWriteChains.set(funnelId, chained.then(() => {}, () => {}));
  return chained;
}


/**
 * Save draft
 *
 * P4-R2C: the payload MUST carry `expected_draft_version`. It arrives through
 * the `...payload` spread, so this hook needs no field of its own -- but a
 * caller that omits it now gets a 400 `EXPECTED_VERSION_REQUIRED` rather than
 * an unversioned write, which is the point.
 */
export function useSaveDraft() {
  const queryClient = useQueryClient();
  
  return useMutation({
    // P4-FIX-797 / D1. NEVER auto-retry this write.
    //
    // The QueryClient this app mounts sets `defaultOptions.mutations.retry: 1`
    // (`lib/queryClient.js`), so without this line React Query resubmits a
    // failed draft save ONCE, before any handler here or on the page sees it.
    // Measured against the project's own client: a failing mutationFn is
    // invoked twice for 503, 409 and 500.
    //
    // That silently defeated P4-FIX-797 on this path. The retry re-sends the
    // SAME `expected_draft_version`, so after a save that COMMITTED and could
    // only not be returned, the second attempt is refused by the compare-and-
    // swap and the page receives DRAFT_VERSION_CONFLICT instead of
    // `draft_committed: true` -- a committed save reported as a conflict. And
    // an UNCERTAIN outcome was resubmitted, which is exactly what must never
    // happen to a non-idempotent write whose effect is unknown.
    //
    // Scoped to THIS hook deliberately. Every other mutation in this file
    // inherits `retry: 1` and is also non-idempotent; changing that default is
    // a repo-wide decision and is tracked separately, not smuggled in here.
    retry: 0,
    mutationFn: async ({ id, ...payload }) => {
      const { data } = await api.put(`/funnels/${id}/draft`, payload);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: funnelKeys.detail(data.id) });
    },
    onError: (error) => {
      // A version conflict is NOT a generic failure toast: the caller owns a
      // conflict surface that explains what to do and keeps the unsaved edits.
      // Toasting here too would double-report it and read as data loss.
      if (isDraftVersionConflict(error)) return;
      // P4-FIX-797. Same reasoning for the two outcomes that are not failures:
      // a CONFIRMED COMMIT and an UNCERTAIN outcome each have their own surface
      // on the page. A generic "Error" toast here would be wrong for the first
      // (the draft was saved) and premature for the second (nobody knows yet).
      if (isDraftSaveCommitted(error) || isDraftSaveUncertain(error)) return;
      toast({
        title: "Error",
        description: apiErrorMessage(error, "Failed to save draft"),
        variant: "destructive",
      });
    },
  });
}

// =============================================================================
// Mutation API Hooks (MA-BE-071: Phase 4A Version Tracking)
// =============================================================================

/**
 * Fetch current draft version for a funnel
 */
export function useFunnelVersion(funnelId) {
  return useQuery({
    queryKey: [...funnelKeys.detail(funnelId), "version"],
    queryFn: async () => {
      const { data } = await api.get(`/funnels/${funnelId}/version`);
      return data;
    },
    enabled: !!funnelId,
    staleTime: 0, // Always fetch fresh version
  });
}

/**
 * P4-FIX-795 -- is Undo/Redo actually available right now?
 *
 * The page used to answer this with `draft_version > 0`. That proxy is wrong:
 * a manual draft save and an AI review approval both advance `draft_version`
 * WITHOUT writing an undoable mutation, so the UI offered an Undo that the
 * server then refused with "No undoable mutation found at current version".
 *
 * Availability is a property of mutation HISTORY, and the server derives each
 * flag from the corresponding operation's own precondition. This asks it.
 *
 * FAIL CLOSED. Any error -- including the 403 a non-Account-admin gets, who
 * may not undo anyway -- yields `false` for both. Never offer an action we
 * could not confirm is available.
 */
export async function fetchUndoAvailability(funnelId) {
  if (!funnelId) return { canUndo: false, canRedo: false, draftVersion: null };
  try {
    const { data } = await api.get(`/funnels/${funnelId}/version`);
    return {
      canUndo: data?.can_undo === true,
      canRedo: data?.can_redo === true,
      draftVersion: Number.isInteger(data?.draft_version) ? data.draft_version : null,
    };
  } catch (error) {
    console.error("Failed to read undo availability:", error);
    return { canUndo: false, canRedo: false, draftVersion: null };
  }
}

/**
 * Check if version has changed (for conflict detection)
 * Returns { hasConflict, currentVersion } 
 */
export async function checkVersionConflict(funnelId, baseVersion) {
  try {
    const { data } = await api.get(`/funnels/${funnelId}/version`);
    return {
      hasConflict: data.draft_version !== baseVersion,
      currentVersion: data.draft_version,
      baseVersion,
    };
  } catch (error) {
    console.error("Failed to check version:", error);
    return { hasConflict: false, currentVersion: baseVersion, baseVersion };
  }
}

/**
 * Execute mutation plan via mutation API
 * Handles version conflicts and returns result
 */
export function useMutate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ funnelId, baseVersion, mutations, summary, mode = "apply_to_draft" }) => {
      const { data } = await api.post(`/funnels/${funnelId}/mutate`, {
        base_version: baseVersion,
        mode,
        summary,
        mutations,
        actor_type: "user",
      });
      return { ...data, funnelId };
    },
    onSuccess: (data) => {
      if (data.status === "applied") {
        queryClient.invalidateQueries({ queryKey: funnelKeys.detail(data.funnelId) });
      }
    },
    onError: (error) => {
      const message = error.response?.data?.detail || "Mutation failed";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });
}

/**
 * Get mutation history for a funnel
 */
export function useMutationHistory(funnelId, limit = 50) {
  return useQuery({
    queryKey: [...funnelKeys.detail(funnelId), "mutations"],
    queryFn: async () => {
      const { data } = await api.get(`/funnels/${funnelId}/mutations`, {
        params: { limit }
      });
      return data;
    },
    enabled: !!funnelId,
  });
}

// =============================================================================
// Undo/Redo Hooks (MA-ARCH-077)
// =============================================================================

/**
 * Undo the most recent mutation
 */
export function useUndo() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ funnelId }) => {
      const { data } = await api.post(`/funnels/${funnelId}/undo`);
      return { ...data, funnelId };
    },
    onSuccess: (data) => {
      if (data.status === "applied") {
        queryClient.invalidateQueries({ queryKey: funnelKeys.detail(data.funnelId) });
        toast({ 
          title: "Undone", 
          description: "Last change reverted",
        });
      }
    },
    onError: (error) => {
      const message = error.response?.data?.detail || "Undo failed";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });
}

/**
 * Redo a previously undone mutation
 */
export function useRedo() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ funnelId }) => {
      const { data } = await api.post(`/funnels/${funnelId}/redo`);
      return { ...data, funnelId };
    },
    onSuccess: (data) => {
      if (data.status === "applied") {
        queryClient.invalidateQueries({ queryKey: funnelKeys.detail(data.funnelId) });
        toast({ 
          title: "Redone", 
          description: "Change reapplied",
        });
      }
    },
    onError: (error) => {
      const message = error.response?.data?.detail || "Redo failed";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });
}

/**
 * Publish funnel
 */
export function usePublishFunnel() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await api.post(`/funnels/${id}/publish`);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: funnelKeys.list() });
      queryClient.invalidateQueries({ queryKey: funnelKeys.detail(data.id) });
      toast({ title: "Funnel published successfully" });
    },
    onError: (error) => {
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : detail?.message || "Failed to publish funnel";
      const errors = Array.isArray(detail?.errors) ? detail.errors : [];
      toast({
        title: "Error",
        description: errors.length ? `${message}: ${errors.join("; ")}` : message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Add node to draft
 */
export function useAddNode() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ funnelId, ...nodeData }) => {
      const { data } = await api.post(`/funnels/${funnelId}/nodes`, nodeData);
      return { ...data, funnelId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: funnelKeys.detail(data.funnelId) });
    },
    onError: (error) => {
      const message = error.response?.data?.detail || "Failed to add node";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });
}

/**
 * Update node
 */
export function useUpdateNode() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ funnelId, nodeId, ...nodeData }) => {
      const { data } = await api.put(`/funnels/${funnelId}/nodes/${nodeId}`, nodeData);
      return { ...data, funnelId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: funnelKeys.detail(data.funnelId) });
    },
    onError: (error) => {
      const message = error.response?.data?.detail || "Failed to update node";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });
}

/**
 * Delete node
 */
export function useDeleteNode() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ funnelId, nodeId }) => {
      await api.delete(`/funnels/${funnelId}/nodes/${nodeId}`);
      return { funnelId, nodeId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: funnelKeys.detail(data.funnelId) });
    },
    onError: (error) => {
      const message = error.response?.data?.detail || "Failed to delete node";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });
}

/**
 * Update node position (lightweight for drag operations)
 */
export function useUpdateNodePosition() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ funnelId, nodeId, x, y }) => {
      const { data } = await api.patch(`/funnels/${funnelId}/nodes/${nodeId}/position`, { x, y });
      return { ...data, funnelId };
    },
    onSuccess: (data) => {
      // Don't invalidate to avoid refetch during drag - update optimistically if needed
    },
    onError: (error) => {
      const message = error.response?.data?.detail || "Failed to update position";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });
}
